import { MONTH_OPTIONS, PERIOD_MODES } from '../constants';
import { formatDisplayDate } from '../helpers';
import { useDraftApplied } from './use-draft-applied';

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const monthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const makeDefault = () => {
  const now = new Date();
  return {
    mode: PERIOD_MODES.MONTH,
    year: now.getFullYear(),
    monthIndex: now.getMonth(),
    rangeStart: monthStart(),
    rangeEnd: today(),
    regions: [],
    countries: [],
    userTypes: [],
  };
};

// The effective query window for a given period state (month mode -> the whole
// month; range mode -> the picked range). Month mode's window is identical to
// what the tabs computed before this hook, so the default view is unchanged.
const windowOf = state =>
  state.mode === PERIOD_MODES.RANGE
    ? { start: state.rangeStart, end: state.rangeEnd }
    : {
        start: new Date(state.year, state.monthIndex, 1),
        end: new Date(state.year, state.monthIndex + 1, 0),
      };

const labelOf = state =>
  state.mode === PERIOD_MODES.RANGE
    ? `${formatDisplayDate(state.rangeStart)} – ${formatDisplayDate(state.rangeEnd)}`
    : `${MONTH_OPTIONS[state.monthIndex].label} ${state.year}`;

// Shared "pick a period" state for the category-style tabs (Category /
// Non-Content / Blogs). Owns the month/range selection + region/country/user-type
// filters behind an Apply button — the table/KPI query reads only the APPLIED
// state, so it fires on Apply/Reset, never on each chip click.
//   * `periodProps` — draft values + handlers + Apply/Reset, spread into <PeriodChips>.
//   * `window` / `label` — the applied query window + heading suffix.
//   * `regions` / `countries` / `userTypes` — the applied filters for the query.
export const usePeriodSelection = () => {
  const { draft, applied, patch, apply, reset, hasUnapplied } = useDraftApplied(
    makeDefault,
  );

  const periodProps = {
    mode: draft.mode,
    onModeChange: mode => patch({ mode }),
    year: draft.year,
    onYearChange: year => patch({ year }),
    monthIndex: draft.monthIndex,
    onMonthChange: monthIndex => patch({ monthIndex }),
    rangeStart: draft.rangeStart,
    rangeEnd: draft.rangeEnd,
    onRangeChange: (rangeStart, rangeEnd) => patch({ rangeStart, rangeEnd }),
    regions: draft.regions,
    onRegionsChange: regions => patch({ regions }),
    countries: draft.countries,
    onCountriesChange: countries => patch({ countries }),
    userTypes: draft.userTypes,
    onUserTypesChange: userTypes => patch({ userTypes }),
    hasUnapplied,
    onApply: apply,
    onReset: reset,
  };

  return {
    periodProps,
    window: windowOf(applied),
    label: labelOf(applied),
    regions: applied.regions,
    countries: applied.countries,
    userTypes: applied.userTypes,
  };
};
